import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { DAY_OF_WEEK } from 'src/features/membershipType/types';
import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/membershipType/utils/constants';
import { useListMembershipAllowedTimes } from 'src/features/allowedTimes/api/membership/useListMembershipAllowedTimes';
import { AllowedTimes } from 'src/features/membershipType/types';
import { bgToEnDays } from 'src/features/allowedTimes/utils/constants';
import { allowedTimesMessages } from 'src/features/allowedTimes/utils/messages';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import CreateMembershipAllowedTimeFormModal from 'src/features/allowedTimes/components/forms/membership/CreateMembershipAllowedTimeFormModal';
import CellMembershipAllowedTimeActions from 'src/features/allowedTimes/components/cell/CellMembershipAllowedTimeActions';
import usePagination from 'src/registry/hooks/usePagination';
import { useParking } from 'src/providers/ParkingProvider';

const columnHelper = createColumnHelper<AllowedTimes>();
const translateSearchValue = (value: string) => {
    if (!value) return value;
    const lower = value.toLowerCase().trim();
    return bgToEnDays[lower] ?? value;
};

type Props = {
    membershipId: string;
};
const MembershipAllowedTimesTable = ({ membershipId }: Props) => {
    const { formatMessage } = useIntl();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination} = usePagination();
    const { parkingId } = useParking();

    //search day_of_week must be sent in the original enum format
    const translatedSearchOptions = {
        search: translateSearchValue(querySearchOptions.search),
    };

    const {
        data: allowedTimesData,
        isLoading,
        isError,
    } = useListMembershipAllowedTimes({
        parkingId,
        membershipId,
        ...translatedSearchOptions,
        ...queryFiltersOptions,
        ...queryPaginationOptions,
        ...querySearchOptions,
        config: { enabled: !!membershipId },
    });

    const weekDayOptions = React.useMemo(
        () =>
            Object.values(DAY_OF_WEEK).map((value) => ({
                label: formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[value]),
                value,
            })),
        [formatMessage],
    );

    const columns = [
        columnHelper.accessor('day_of_week', {
            enableHiding: true,
            header: DataTableColumnHeader,
            cell: (props) => {
                const dayOfWeek = props.row.original.day_of_week;
                return formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[dayOfWeek]) ?? '-';
            },
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(allowedTimesMessages.dayOfWeek),
                options: weekDayOptions,
            },
        }),
        columnHelper.accessor('from_time', {
            enableHiding: true,
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(allowedTimesMessages.from),
            },
        }),
        columnHelper.accessor('to_time', {
            enableHiding: true,
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(allowedTimesMessages.to),
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: CellMembershipAllowedTimeActions,
        }),
    ];

    return (
        <DataTable
            pagination={pagination}
            isLoading={isLoading}
            isError={isError}
            columns={columns as any}
            data={allowedTimesData}
            search={search}
            filters={filters}
            enableSearchParams={false}
            enableLocalFilters={false}
            semantics={{
                title: formatMessage(allowedTimesMessages.allowedTime),
                entryName: formatMessage(allowedTimesMessages.time).toLowerCase(),
                entryNames: formatMessage(allowedTimesMessages.times).toLowerCase(),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                newEntry: CreateMembershipAllowedTimeFormModal,
            }}
        />
    );
};

export default MembershipAllowedTimesTable;
